import React, { useMemo } from 'react';
import { styled, Grid, makeStyles, Box } from '@material-ui/core';
import { useIntl } from 'react-intl';

import translations from 'utils/translations';
import { SdmRichText, SdmImageSwiper } from '..';
import { Caption } from '../Caption';

import { DataSheet } from './DataSheet';
import { theme, Typography } from './mui-overrides';

/*
type WithCaption<T> = {
  media: T;
  overview: RichText["text"];
};

type ImageWithAlt = {
  img: string;
  alt: string;
};

type RichText = {
  _tag: 'rich-text';
  text: any
};

type TextBlock = RichText & {
  _tag: 'text-block';
};

type Quote = RichText & {
  _tag: 'quote';
};

type ImageSlider = {
  _tag: 'image-slider';
  imgs: Array<WithCaption<ImageWithAlt>>;
};

type EmbededYoutubeVideo = {
  _tag: 'embeded-youtube-video';
} & WithCaption<string>;

type EmbededVimeoVideo = {
  _tag: 'embeded-vimeo-video';
} & WithCaption<string>;

type BrandViewProps = {
  name: string;
  cover: ImageWithAlt;
  overview: RichText;
  contents: (
    | TextBlock
    | Quote
    | ImageSlider
    | EmbededYoutubeVideo
    | EmbededVimeoVideo
  )[];
};

*/

const useStyles = makeStyles(() => ({
  textBlock: {
    ...theme.typography.textblock,
    '& div[data-contents=true] > div[data-block=true] > div[data-offset-key]': {
      padding: 0,
      marginBottom: 10,
    },
    '& div[data-contents=true] > div[data-block=true]:last-of-type > div[data-offset-key]':
      {
        marginBottom: 0,
      },
  },
  yGutters: {
    ['@media (min-width: 768px)']: {
      paddingTop: 25,
      paddingBottom: 25,
    },
    paddingTop: 15,
    paddingBottom: 15,
  },
  dividerTop: {
    ['@media (min-width: 768px)']: {
      paddingTop: 25,
    },
    paddingTop: 15,
  },
  paragraph: {
    overflow: 'hidden',
    ['@media (min-width: 768px)']: {
      paddingTop: 80,
      paddingBottom: 80,
      backgroundColor: theme.palette.background.grey,
    },
  },
  showroom: {
    ['@media (min-width: 992px)']: {
      paddingTop: 64,
      paddingBottom: 64,
    },
    paddingTop: 32,
    paddingBottom: 32,
    backgroundColor: '#000',
    color: '#fff',
  },
  quote: {
    paddingLeft: 15,
    borderLeft: `10px solid ${theme.palette.primary.main}`,
  },
}));

export const Video = styled('iframe')({
  width: '100%',
  height: 'auto',
  minHeight: 500,
});

export const Divider = styled('div')(({ theme }) => ({
  ['@media screen and (min-width: 992px)']: {
    marginTop: 24,
  },
  margin: 0,
  height: '2px',
  backgroundColor: theme.palette.primary.main,
  width: '200px',
  marginTop: '16px',
  marginBottom: '16px',
}));

export const Container = styled('div')(({ flex, direction, yGutters }) => ({
  ['@media (min-width: 768px)']: {
    width: 750,
    ...(yGutters && {
      paddingTop: 25,
      paddingBottom: 25,
    }),
  },
  ['@media (min-width: 992px)']: {
    width: 970,
  },
  ['@media (min-width: 1280px)']: {
    width: 1280,
  },
  margin: 'auto',
  paddingLeft: 15,
  paddingRight: 15,
  ...(flex && { display: 'flex', flexDirection: direction }),
  ...(yGutters && {
    paddingTop: 15,
    paddingBottom: 15,
  }),
}));

export const Row = ({ children, itemProps, className }) => {
  return (
    <Grid className={className} container item xs={12} justify="center">
      <Grid item xs={12} md={8} {...itemProps}>
        {children}
      </Grid>
    </Grid>
  );
};

export const TextBlock = ({ text }) => {
  const classes = useStyles();

  return (
    <Container flex className={classes.yGutters}>
      <Row>
        <SdmRichText
          variant="white"
          editorStyleOverride={{
            maxHeight: 'none',
            marginRight: 0,
            paddingLeft: 0,
            minHeight: 'none !important',
          }}
          formControlStyle={{
            margin: 0,
          }}
          labelStyle={{
            display: 'none',
          }}
          editorClassName={classes.textBlock}
          value={text}
          readOnly
        />
      </Row>
    </Container>
  );
};

export const Quote = ({ text }) => {
  const classes = useStyles();

  return (
    <Container className={classes.yGutters}>
      <Row
        itemProps={{
          container: true,
          direction: 'column',
        }}
      >
        <Typography className={classes.quote} variant="quote">
          {text}
        </Typography>
      </Row>
    </Container>
  );
};

const VideoCaptionContainer = styled('div')({
  '@media screen and (min-width: 992px)': {
    marginTop: 45,
  },
  marginTop: 28,
});

export const EmbededVideo = ({ src, caption }) => {
  const classes = useStyles();
  const parseYoutubeVideo = (src) => {
    const srcId = src?.split('/')?.pop();
    return srcId && !src?.includes('embed')
      ? `https://www.youtube.com/embed/${srcId}?rel=0&enablejsapi=1"`
      : src;
  };
  const isYoutubeVideo = !src?.includes('vimeo');

  const parsedSrc = isYoutubeVideo ? parseYoutubeVideo(src) : src;

  return (
    <Grid
      component={Container}
      container
      item
      xs={12}
      className={classes.yGutters}
    >
      <Row>
        {caption && (
          <VideoCaptionContainer>
            <Caption text={caption} variant="video" />
          </VideoCaptionContainer>
        )}
        <div className={classes.dividerTop}>
          <Video src={parsedSrc} />
        </div>
      </Row>
    </Grid>
  );
};

const ImageCaptionContainer = styled('div')({
  '@media screen and (min-width: 992px)': {
    paddingLeft: '10%',
    paddingRight: '10%',
  },
  display: 'flex',
  flexDirection: 'column',
});

const Image = styled('img')({
  maxWidth: '100%',
  height: 'auto',
});

export const ImageCaption = ({ src, alt, caption }) => {
  const classes = useStyles();

  return (
    <Container flex direction="column" className={classes.yGutters}>
      <Row
        itemProps={{
          md: 10,
        }}
      >
        <ImageCaptionContainer>
          <Box margin="auto">
            <Image src={src} alt={alt} />
            {caption && <Caption text={caption} />}
          </Box>
        </ImageCaptionContainer>
      </Row>
    </Container>
  );
};

const ImageFullWidth = styled('img')({
  width: '100%',
});

export const FullwidthImage = ({ src, alt }) => {
  const classes = useStyles();

  return (
    <Grid item xs={12} className={classes.yGutters}>
      <ImageFullWidth src={src} alt={alt} />
    </Grid>
  );
};

export const ImageSlider = ({ images }) => {
  const classes = useStyles();

  return (
    <Box width="100%" className={classes.yGutters}>
      <Box className={classes.paragraph}>
        <SdmImageSwiper
          images={images
            .filter((a) => a)
            .sort((a, b) => a.position - b.position)}
        />
      </Box>
    </Box>
  );
};

export const Showroom = ({ title, src }) => {
  const intl = useIntl();
  const i18n = translations.showroom(intl);
  const classes = useStyles();

  return (
    <div className={classes.showroom}>
      <Container flex>
        <Row
          itemProps={{
            md: 10,
          }}
        >
          <Typography variant="showroomSubtitle">{i18n.subtitle}</Typography>
          <Typography variant="showroomTitle">{title}</Typography>
          <Box paddingX="15px">
            <iframe
              src={src}
              style={{
                width: '100%',
                minHeight: 600,
              }}
            />
          </Box>
        </Row>
      </Container>
    </div>
  );
};

const Designer = ({ name, src, alt }) => {
  const intl = useIntl();
  const i18n = translations.designer(intl);
  const classes = useStyles();

  return (
    <div className={classes.yGutters}>
      <Container>
        <Row>
          <Typography variant="designerSubtitle">{i18n.designedBy}</Typography>
          <Typography variant="designerTitle">{name}</Typography>
        </Row>
      </Container>
      <Box paddingTop="30px">
        <Image src={src} alt={alt} />
      </Box>
    </div>
  );
};

const TagToComponentMap = {
  'embed-video': EmbededVideo,
  'text-block': TextBlock,
  quote: Quote,
  'image-slider': ImageSlider,
  'full-width-image': FullwidthImage,
  'image-caption': ImageCaption,
  'data-sheet': DataSheet,
  showroom: Showroom,
  designer: Designer,
};

export const Contents = ({ contents: contentsProp }) => {
  const contents = useMemo(() => {
    return contentsProp.map((el, i) => {
      const { _tag, ...rest } = el;
      const Component = TagToComponentMap[_tag];

      return <Component key={`${_tag}-${i}`} {...rest} />;
    });
  }, [contentsProp]);

  return <React.Fragment>{contents}</React.Fragment>;
};
